/**
 * Paneles propios de una solapa: el único punto de extensión del formulario.
 *
 * Por qué existe
 * --------------
 * El formulario es **uno solo para todos los modelos** (ARQUITECTURA.md §5) y
 * dibuja lo que la metadata declara: bloques de campos y grillas. El 99 % de las
 * pantallas del ERP entran ahí y no necesitan una línea de código propio.
 *
 * Hay un caso que no entra: cuando la solapa necesita mostrar algo que **no es
 * un campo del registro** —un número calculado por una acción de servidor, un
 * botón que abre un selector con estado propio—. Escribirlo dentro de
 * `FormView` con un `if (spec.model === 'base.partner')` sería exactamente lo
 * que este proyecto no hace: un componente por modelo escondido en el renderer
 * genérico.
 *
 * La forma en la que sí entra
 * ---------------------------
 * Un **registro** por `(modelo, etiqueta de solapa)`, igual que
 * `components/fields/registry.ts` hace con los widgets. `FormView` hace UNA
 * consulta genérica —«¿hay panel para esta solapa?»— y no sabe qué modelos hay
 * en la tabla ni qué dibujan. Agregar un panel para otra solapa es agregar una
 * fila acá; sacarlo es sacarla, y el formulario sigue funcionando igual.
 *
 * La clave es la **etiqueta** de la solapa y no su índice: el backend puede
 * reordenar las solapas de un modelo (el orden lo decide quien extiende el
 * `ModelSpec`) y un índice haría que el panel apareciera en otra solapa sin que
 * nadie se enterara. Si la solapa se renombra en el backend, el panel deja de
 * dibujarse — que es preferible a dibujarse en el lugar equivocado.
 */

import type { ReactNode } from 'react';

import { VentaRapidaPanel } from '@/views/VentaRapidaPanel';
import { VentasPorConsorcioPanel } from '@/views/VentasPorConsorcioPanel';

/** Lo que recibe un panel de solapa: el registro que se está mirando. */
export interface TabPanelProps {
  /** Id del registro abierto, o `null` si es un alta sin guardar. */
  recordId: number | null;
}

/**
 * Modelo → etiqueta de solapa → panel.
 *
 * Dos entradas. La tabla existe porque la alternativa —un condicional dentro del
 * renderer— es la que no escala, y la segunda entrada es la prueba: se agregó un
 * panel entero sin tocar una línea de `FormView`.
 */
const PANELES: Record<string, Record<string, (props: TabPanelProps) => ReactNode>> = {
  'base.partner': {
    // La solapa «Ventas» la agrega el módulo `sales` al `ModelSpec` de
    // `base.partner` (ver `sales/schema.py::extender_ficha_de_contacto`).
    Ventas: ({ recordId }) => <VentaRapidaPanel recordId={recordId} />,
  },
  'property.administrador': {
    // La solapa la declara `property_mgmt/schema.py` en `ADMINISTRADOR_SPEC`,
    // **sin un solo campo**: lo que se dibuja no es un dato del registro sino un
    // cuadro calculado por la acción `ventas_por_consorcio`. La clave es la
    // etiqueta, así que si allá se renombra la solapa, acá hay que renombrarla
    // también — el panel deja de dibujarse, que es preferible a dibujarse en la
    // solapa equivocada.
    'Ventas por consorcio': ({ recordId }) => <VentasPorConsorcioPanel recordId={recordId} />,
  },
};

/**
 * El panel de esta solapa, o `null` si no tiene.
 *
 * Se dibuja **antes** de los bloques de campos: los totales y el botón de vender
 * son el encabezado de la solapa, y la grilla de lo vendido es el detalle.
 */
export function TabPanel({
  model,
  tabLabel,
  recordId,
}: {
  model: string;
  tabLabel: string;
  recordId: number | null;
}): ReactNode {
  const panel = PANELES[model]?.[tabLabel];
  if (panel === undefined) return null;
  return panel({ recordId });
}
